import React, {useEffect, useRef} from 'react';
import api from './api';
import {useBulkJobsDispatch, useBulkJobsState} from '../contexts/BulkJobsContext';
import {bulkJobIsActive, bulkJobIsTerminal, bulkJobTerminalAttrs} from './helpers/removalOutcome';
import {useTranslation} from 'react-i18next';

const ProgressPoll = ({guid, organizationId, interval, dispatch}) => {
    const {t} = useTranslation();
    const failureMessage = t('message-bulk-job-failed', 'Bulk job failed.');
    useEffect(() => {
        let disposed = false;
        let timer;
        let retryDelay = interval;
        const controller = new AbortController();
        const poll = async () => {
            try {
                const {data: job} = await api.get(`/api/bulk_jobs/${guid}`, {signal: controller.signal});
                if (disposed) return;
                if (job.guid === guid && String(job.organization_id) === String(organizationId)) {
                    dispatch({...job, ...bulkJobTerminalAttrs(job, failureMessage), type: 'progress'});
                }
                retryDelay = interval;
            } catch (_error) {
                // Keep observing after transient failures, without overlapping requests.
                retryDelay = Math.min(retryDelay * 2, 30000);
            }
            if (!disposed) timer = setTimeout(poll, retryDelay);
        };
        poll();
        return () => {
            disposed = true;
            clearTimeout(timer);
            controller.abort();
        };
    }, [guid, organizationId, interval, dispatch, failureMessage]);
    return null;
};

// Lives alongside ActionCable, outside tree rows and popovers. A dropped socket
// or a remounted drag target must not strand an operation or its count refresh.
export default function BulkJobMonitor({organizationId, userId, onTerminal}) {
    const {bulkJobs} = useBulkJobsState();
    const dispatch = useBulkJobsDispatch();
    const scope = `${organizationId}:${userId}`;
    const notified = useRef({scope, jobs: new Map()});
    // An action is supplied by BulkJob API snapshots. Other imports also send
    // bulkJobProgress events, but their GUIDs do not belong to /api/bulk_jobs.
    const jobs = organizationId && userId ? Object.values(bulkJobs).filter(job =>
        job.action && String(job.organization_id) === String(organizationId)
    ) : [];

    useEffect(() => {
        if (notified.current.scope !== scope) notified.current = {scope, jobs: new Map()};
        jobs.forEach(job => {
            if (!bulkJobIsTerminal(job)) {
                notified.current.jobs.delete(job.guid);
                return;
            }
            // Notify again when a completed move finishes its indexing phase.
            const version = `${job.aasm_state}:${!!job.indexing_pending}`;
            if (notified.current.jobs.get(job.guid) === version) return;
            notified.current.jobs.set(job.guid, version);
            onTerminal({...job, ...bulkJobTerminalAttrs(job), type: 'bulkJobProgress'});
        });
    }, [bulkJobs, scope, onTerminal]);

    return jobs.filter(bulkJobIsActive).map(job => <ProgressPoll
        key={`${scope}:${job.guid}`}
        guid={job.guid}
        organizationId={organizationId}
        interval={bulkJobIsTerminal(job) && job.indexing_pending ? 30000 : 5000}
        dispatch={dispatch}
    />);
}
